import axios from 'axios';
import authService from './auth';

// Configuración de la API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://27432rocib.execute-api.us-east-2.amazonaws.com/dev/';
const API_TIMEOUT = 40000; // 40 segundos (aumentado para queries grandes con paginación)

// La aplicación usa el token de Cognito en Authorization. Nunca incluir API
// keys de servicio en el bundle: el backend valida las credenciales heredadas
// exclusivamente contra SSM y rechaza cualquier fallo de consulta.

// Crear instancia de axios
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación
api.interceptors.request.use(
  (config) => {
    // Agregar token de autenticación si existe
    const token = authService.getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // En demostración no hay token que refrescar: un 401 aquí significa que
    // algo intentó hablar con la API real, no que la sesión haya caducado.
    // Sin esta guarda, el interceptor cerraría la sesión y expulsaría al
    // usuario de la demo al primer intento.
    if (authService.isDemo?.()) {
      return Promise.reject(error);
    }

    // Si el error es 401 y no hemos intentado refrescar el token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Intentar refrescar el token
        await authService.refreshToken();
        
        // Reintentar la petición original con el nuevo token
        const token = authService.getIdToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;

        return api(originalRequest);
      } catch (refreshError) {
        // Si falla el refresh, cerrar sesión
        authService.logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ========================================
// ENDPOINTS DE DISPOSITIVOS
// ========================================

/**
 * Obtener lista de dispositivos
 */
export const getDevices = (params = {}) => {
  return api.get('devices', { params }).then(res => res.data);
};

/**
 * Obtener información de un dispositivo específico
 */
export const getDevice = (deviceId) => {
  return api.get(`devices/${deviceId}`).then(res => res.data);
};

/**
 * Obtener telemetría de un dispositivo (datos crudos - tiene límite de 6MB)
 */
export const getDeviceTelemetry = (deviceId, limit = 1, hours = 24) => {
  return api.get(`devices/${deviceId}/telemetry`, { 
    params: { limit, hours } 
  }).then(res => res.data);
};

/**
 * Obtener telemetría AGREGADA de un dispositivo (datos procesados en servidor).
 *
 * Acepta dos formas:
 *   - getAggregatedTelemetry(deviceId, 7)              → últimos 7 días (compat)
 *   - getAggregatedTelemetry(deviceId, { days: 30 })   → últimos 30 días
 *   - getAggregatedTelemetry(deviceId, { startDate: '2025-11-01', endDate: '2025-11-30' })
 *
 * @param {string} deviceId - ID del dispositivo
 * @param {number|{ days?: number, startDate?: string, endDate?: string }} options
 * @returns {Promise<Object>} Datos agregados por día con desglose por hora
 */
export const getAggregatedTelemetry = (deviceId, options = 7) => {
  const params = {};

  if (typeof options === 'number') {
    params.days = options;
  } else if (options && typeof options === 'object') {
    if (options.startDate) {
      params.start_date = options.startDate;
      if (options.endDate) params.end_date = options.endDate;
    } else if (typeof options.days === 'number') {
      params.days = options.days;
    } else {
      params.days = 7;
    }
  } else {
    params.days = 7;
  }

  return api.get(`devices/${deviceId}/telemetry/aggregated`, {
    params,
    timeout: 60000,
  }).then(res => res.data);
};

/**
 * Atajo para consultar un mes completo (zona horaria UTC-6).
 * @param {string} deviceId
 * @param {number} year - año (YYYY)
 * @param {number} month - mes 1-12
 */
export const getAggregatedTelemetryByMonth = (deviceId, year, month) => {
  const m = String(month).padStart(2, '0');
  const startDate = `${year}-${m}-01`;
  // último día del mes
  const lastDay = new Date(year, month, 0).getDate();
  const endDate = `${year}-${m}-${String(lastDay).padStart(2, '0')}`;
  return getAggregatedTelemetry(deviceId, { startDate, endDate });
};

/**
 * Obtener estadísticas de un dispositivo
 */
export const getDeviceStatistics = (deviceId, hours = 24) => {
  return api.get(`devices/${deviceId}/statistics`, { 
    params: { hours } 
  }).then(res => res.data);
};

/**
 * Obtener configuración de un dispositivo
 */
export const getDeviceConfig = (deviceId) => {
  return api.get(`devices/${deviceId}/config`).then(res => res.data);
};

/**
 * Actualizar configuración de un dispositivo
 */
export const updateDeviceConfig = (deviceId, config) => {
  return api.post(`devices/${deviceId}/config`, config).then(res => res.data);
};

/**
 * Obtener salud del dispositivo
 */
export const getDeviceHealth = (deviceId) => {
  return api.get(`devices/${deviceId}/health`).then(res => res.data);
};

/**
 * Obtener información del sistema del dispositivo
 */
export const getDeviceSystemInfo = (deviceId) => {
  return api.get(`devices/${deviceId}/system/info`).then(res => res.data);
};

/**
 * Reiniciar dispositivo
 */
export const restartDevice = (deviceId) => {
  return api.post(`devices/${deviceId}/system/restart`).then(res => res.data);
};

// ========================================
// ENDPOINTS DE VÁLVULA
// ========================================

/**
 * Obtener estado de la válvula
 */
export const getValveStatus = (deviceId) => {
  return api.get(`devices/${deviceId}/valve/status`).then(res => res.data);
};

/**
 * Controlar válvula
 * @param {string} deviceId - ID del dispositivo
 * @param {string} action - Acción explícita: 'open' o 'close'
 */
export const controlValve = (deviceId, action) => {
  if (action !== 'open' && action !== 'close') {
    return Promise.reject(new Error('La acción debe ser open o close'));
  }
  return api.post(`devices/${deviceId}/valve/control`, { action }).then(res => res.data);
};

// API de válvula (compatibilidad con código existente)
export const valveAPI = {
  getStatus: (deviceId) => getValveStatus(deviceId),
  open: (deviceId) => controlValve(deviceId, 'open'),
  close: (deviceId) => controlValve(deviceId, 'close'),
};

// ========================================
// ENDPOINTS DE ALERTAS
// ========================================

/**
 * Obtener alertas de un dispositivo
 */
export const getDeviceAlerts = (deviceId, params = {}) => {
  const queryParams = {
    limit: params.limit || 50,
    resolved: params.resolved !== undefined ? params.resolved.toString() : 'false',
    severity: params.severity || undefined
  };
  
  return api.get(`devices/${deviceId}/alerts`, { params: queryParams }).then(res => res.data);
};

/**
 * NO DESPLEGADO. Este recurso no existe en API Gateway (comprobado el
 * 2026-08-21). Llamarlo no devuelve 404 sino un fallo de CORS, porque API
 * Gateway responde 403 a una ruta desconocida y el preflight nunca llega a
 * devolver 200. Hoy no lo usa nadie; antes de conectarlo a un boton hay que
 * crear el recurso.
 */
export const getAllAlerts = (params = {}) => {
  return api.get('alerts', { params }).then(res => res.data);
};

/**
 * Resolver una alerta
 */
export const resolveAlert = (deviceId, alertId) => {
  // El _ es seguro en URLs, no necesita encoding
  // Pero por si acaso, usamos encodeURIComponent solo en el alertId
  return api.post(`devices/${deviceId}/alerts/${alertId}/resolve`).then(res => res.data);
};

/**
 * Obtener alertas activas de un dispositivo (sin resolver)
 */
export const getActiveAlerts = (deviceId) => {
  return getDeviceAlerts(deviceId, { resolved: false, limit: 20 });
};

/**
 * Obtener alertas resueltas de un dispositivo
 */
export const getResolvedAlerts = (deviceId) => {
  return getDeviceAlerts(deviceId, { resolved: true, limit: 20 });
};

/**
 * Obtener alertas críticas de un dispositivo
 */
export const getCriticalAlerts = (deviceId) => {
  return getDeviceAlerts(deviceId, { resolved: false, severity: 'critical', limit: 10 });
};

// ========================================
// ENDPOINTS DE USUARIO
// ========================================

/**
 * Obtener perfil del usuario
 */
export const getUserProfile = () => {
  return api.get('auth/profile').then(res => res.data);
};

/**
 * Actualizar perfil del usuario
 */
export const updateUserProfile = (data) => {
  return api.put('auth/profile', data).then(res => res.data);
};

/**
 * Obtener lista de usuarios (admin)
 */
export const getUsers = () => {
  return api.get('users').then(res => res.data);
};

/**
 * Obtener dispositivos de un usuario
 */
export const getUserDevices = (userId) => {
  return api.get(`users/${userId}/devices`).then(res => res.data);
};

// ========================================
// ENDPOINTS DE EXPORTACIÓN
// ========================================

/**
 * NO DESPLEGADO. Este recurso no existe en API Gateway (comprobado el
 * 2026-08-21). Llamarlo no devuelve 404 sino un fallo de CORS, porque API
 * Gateway responde 403 a una ruta desconocida y el preflight nunca llega a
 * devolver 200. Hoy no lo usa nadie; antes de conectarlo a un boton hay que
 * crear el recurso.
 */
export const exportStatistics = (deviceId, format = 'json') => {
  return api.get(`devices/${deviceId}/export/statistics`, {
    params: { format },
    responseType: format === 'csv' ? 'blob' : 'json'
  }).then(res => res.data);
};

/**
 * NO DESPLEGADO. Este recurso no existe en API Gateway (comprobado el
 * 2026-08-21). Llamarlo no devuelve 404 sino un fallo de CORS, porque API
 * Gateway responde 403 a una ruta desconocida y el preflight nunca llega a
 * devolver 200. Hoy no lo usa nadie; antes de conectarlo a un boton hay que
 * crear el recurso.
 */
export const exportLogs = (deviceId, limit = 100, format = 'json') => {
  return api.get(`devices/${deviceId}/export/logs`, {
    params: { limit, format },
    responseType: format === 'csv' ? 'blob' : 'json'
  }).then(res => res.data);
};

// ========================================
// FUNCIÓN AUXILIAR PARA MEDICIONES (compatibilidad)
// ========================================

/**
 * Estimar presión del agua a partir del flujo cuando el sensor no la reporta.
 * Modelo simplificado: la red residencial mexicana ronda 45-55 psi en reposo
 * y cae linealmente con el caudal demandado.
 */
export const estimateWaterPressure = (flowRateLpm = 0, valveOpen = true) => {
  if (!valveOpen) return 0;
  const baseline = 52; // psi en reposo
  const drop = Math.min(flowRateLpm * 0.25, 18); // hasta 18 psi de caída con flujo alto
  const noise = (Math.sin(Date.now() / 7000) + 1) * 1.2; // ondulación natural
  return Math.max(0, baseline - drop + noise);
};

/**
 * Obtener mediciones de flujo (compatibilidad con código existente)
 * ACTUALIZADO: Ahora retorna consumos por período + presión del agua
 */
export const obtenerMediciones = (deviceId) => {
  if (!deviceId) {
    console.warn('obtenerMediciones: deviceId no proporcionado');
    return Promise.resolve({ 
      flow_rate_lpm: 0,
      daily_consumption: 0,
      weekly_consumption: 0,
      monthly_consumption: 0,
      pressure_psi: 0
    });
  }
  
  return getDeviceTelemetry(deviceId, 1)
    .then(data => {
      if (data && data.data && data.data.length > 0) {
        const telemetry = data.data[0];
        const flowRate = telemetry.flow_rate_lpm || 0;
        const valveOpen = telemetry.valve_open !== undefined
          ? telemetry.valve_open
          : (telemetry.valve_state ? telemetry.valve_state !== 'closed' : true);

        // Presión: SÓLO la que reporte el sensor.
        //
        // Antes, si el dispositivo no la enviaba, se estimaba a partir del
        // caudal con una función que además le añade ondulación aleatoria. El
        // firmware actual no mide presión, así que en la práctica ese valor
        // inventado se mostraba siempre como si fuera una lectura real.
        // Sin sensor se devuelve null y la interfaz lo presenta como no
        // disponible, que es la verdad.
        const pressureFromSensor = telemetry.pressure_psi
          ?? telemetry.water_pressure_psi
          ?? telemetry.pressure;
        const pressure_psi = pressureFromSensor !== undefined && pressureFromSensor !== null
          ? Number(pressureFromSensor)
          : null;

        return {
          // Flujo en tiempo real
          flow_rate_lpm: flowRate,
          flow_liters_total: telemetry.flow_liters_total || telemetry.flow_liters || 0,
          
          // Consumos por período
          daily_consumption: telemetry.daily_consumption || 0,
          weekly_consumption: telemetry.weekly_consumption || 0,
          monthly_consumption: telemetry.monthly_consumption || 0,
          
          // Duración de flujo (para alertas)
          flow_duration_minutes: telemetry.flow_duration_minutes || 0,
          
          // Otros datos
          pulse_count: telemetry.pulse_count || 0,
          valve_open: valveOpen,
          valve_state: telemetry.valve_state || 'unknown',
          timestamp: telemetry.timestamp,

          // NUEVO: Presión del agua (psi)
          pressure_psi
        };
      }
      return { 
        flow_rate_lpm: 0,
        daily_consumption: 0,
        weekly_consumption: 0,
        monthly_consumption: 0,
        pressure_psi: 0
      };
    })
    .catch(error => {
      console.error('Error obteniendo mediciones:', error);
      return { 
        flow_rate_lpm: 0,
        daily_consumption: 0,
        weekly_consumption: 0,
        monthly_consumption: 0,
        pressure_psi: 0
      };
    });
};

/**
 * Obtener datos agregados por día/semana/mes
 */
export const getAggregatedData = (deviceId, period = 'day', limit = 30) => {
  // Por ahora, obtener telemetría y agregarla en el cliente
  // TODO: Implementar endpoint de agregación en backend cuando esté disponible
  const hours = period === 'day' ? 24 : period === 'week' ? 168 : 720;
  
  return getDeviceTelemetry(deviceId, 1000, hours)
    .then(data => {
      if (!data || !data.data || data.data.length === 0) {
        return { aggregated_data: [], period };
      }
      
      // Agregar datos por período
      return aggregateDataByPeriod(data.data, period);
    });
};

/**
 * Función auxiliar para agregar datos por período
 */
function aggregateDataByPeriod(telemetryData, period) {
  const aggregated = {};
  
  telemetryData.forEach(item => {
    const date = new Date(item.timestamp * 1000);
    let key;
    
    if (period === 'day') {
      key = date.toISOString().split('T')[0]; // YYYY-MM-DD
    } else if (period === 'week') {
      const weekNumber = getWeekNumber(date);
      key = `${date.getFullYear()}-W${weekNumber}`;
    } else if (period === 'month') {
      key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }
    
    if (!aggregated[key]) {
      aggregated[key] = {
        period: key,
        consumption: 0,
        flow_rate_avg: 0,
        flow_rate_max: 0,
        count: 0
      };
    }
    
    aggregated[key].consumption = Math.max(
      aggregated[key].consumption, 
      parseFloat(item.daily_consumption || item.flow_liters_total || 0)
    );
    aggregated[key].flow_rate_avg += parseFloat(item.flow_rate_lpm || 0);
    aggregated[key].flow_rate_max = Math.max(
      aggregated[key].flow_rate_max, 
      parseFloat(item.flow_rate_lpm || 0)
    );
    aggregated[key].count++;
  });
  
  // Calcular promedios
  Object.keys(aggregated).forEach(key => {
    aggregated[key].flow_rate_avg /= aggregated[key].count;
  });
  
  return {
    aggregated_data: Object.values(aggregated).sort((a, b) => 
      a.period.localeCompare(b.period)
    ),
    period
  };
}

/**
 * Obtener número de semana del año
 */
function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// ========================================
// UTILIDADES
// ========================================

/**
 * Verificar si la API está configurada correctamente
 */
export const isApiConfigured = () => {
  return API_BASE_URL && !API_BASE_URL.includes('YOUR-API-ID');
};

/**
 * Obtener URL base de la API
 */
export const getApiBaseUrl = () => {
  return API_BASE_URL;
};

export default api;
